/**
 * REST Waypoint Data Provider — resolves waypoints via the FAA NASR API
 * with a static fallback for offline/development use.
 */
import type { WaypointDataProvider, ResolvedWaypoint } from '@efb/shared-core';

const NASR_API_BASE = 'https://nfdc.faa.gov/nfdcApps/services/ajv5';

/**
 * Static fallback with common US airports for dev/offline use.
 */
const STATIC_AIRPORTS: ResolvedWaypoint[] = [
  { identifier: 'KJFK', name: 'John F Kennedy Intl', type: 'airport', latitude: 40.6399, longitude: -73.7787, elevation: 13, longestRunwayFt: 14511, source: 'nasr' },
  { identifier: 'KLAX', name: 'Los Angeles Intl', type: 'airport', latitude: 33.9425, longitude: -118.4081, elevation: 128, longestRunwayFt: 12091, source: 'nasr' },
  { identifier: 'KORD', name: 'Chicago O Hare Intl', type: 'airport', latitude: 41.9742, longitude: -87.9073, elevation: 672, longestRunwayFt: 13000, source: 'nasr' },
  { identifier: 'KATL', name: 'Hartsfield Jackson Atlanta Intl', type: 'airport', latitude: 33.6407, longitude: -84.4277, elevation: 1026, longestRunwayFt: 12390, source: 'nasr' },
  { identifier: 'KDEN', name: 'Denver Intl', type: 'airport', latitude: 39.8561, longitude: -104.6737, elevation: 5431, longestRunwayFt: 16000, source: 'nasr' },
  { identifier: 'KSFO', name: 'San Francisco Intl', type: 'airport', latitude: 37.6188, longitude: -122.3750, elevation: 13, longestRunwayFt: 11870, source: 'nasr' },
  { identifier: 'KLAS', name: 'Harry Reid Intl', type: 'airport', latitude: 36.0840, longitude: -115.1537, elevation: 2181, longestRunwayFt: 14510, source: 'nasr' },
  { identifier: 'KPHX', name: 'Phoenix Sky Harbor Intl', type: 'airport', latitude: 33.4342, longitude: -112.0116, elevation: 1135, longestRunwayFt: 11489, source: 'nasr' },
  { identifier: 'KDFW', name: 'Dallas Fort Worth Intl', type: 'airport', latitude: 32.8968, longitude: -97.0380, elevation: 607, longestRunwayFt: 13401, source: 'nasr' },
  { identifier: 'KMIA', name: 'Miami Intl', type: 'airport', latitude: 25.7959, longitude: -80.2870, elevation: 8, longestRunwayFt: 13000, source: 'nasr' },
  { identifier: 'KOSH', name: 'Wittman Regional', type: 'airport', latitude: 43.9844, longitude: -88.5570, elevation: 808, longestRunwayFt: 6179, source: 'nasr' },
  { identifier: 'KLGA', name: 'LaGuardia', type: 'airport', latitude: 40.7772, longitude: -73.8726, elevation: 22, longestRunwayFt: 7003, source: 'nasr' },
  { identifier: 'KEWR', name: 'Newark Liberty Intl', type: 'airport', latitude: 40.6925, longitude: -74.1687, elevation: 18, longestRunwayFt: 11000, source: 'nasr' },
  { identifier: 'KBOS', name: 'General Edward Lawrence Logan Intl', type: 'airport', latitude: 42.3656, longitude: -71.0096, elevation: 19, longestRunwayFt: 10083, source: 'nasr' },
  { identifier: 'KSEA', name: 'Seattle-Tacoma Intl', type: 'airport', latitude: 47.4502, longitude: -122.3088, elevation: 433, longestRunwayFt: 11901, source: 'nasr' },
];

export class RestWaypointDataProvider implements WaypointDataProvider {
  private useStaticFallback = true;

  async resolveIdentifier(identifier: string): Promise<ResolvedWaypoint | null> {
    const id = identifier.trim().toUpperCase();

    // Try static fallback first
    const staticResult = STATIC_AIRPORTS.find(a => a.identifier === id);
    if (staticResult) return staticResult;

    if (this.useStaticFallback) return null;

    try {
      const resp = await fetch(`${NASR_API_BASE}/airportSearch.jsp?searchAirportIdent=${id}`);
      if (!resp.ok) return null;
      const data = await resp.json();
      if (!data || !data.length) return null;
      const r = data[0];
      return {
        identifier: r.icaoId || r.facilityId || id,
        name: r.facilityName || id,
        type: 'airport',
        latitude: Number(r.latitude) || 0,
        longitude: Number(r.longitude) || 0,
        elevation: Number(r.elevation) || undefined,
        source: 'nasr',
      };
    } catch {
      return null;
    }
  }

  async searchByName(query: string, limit = 20): Promise<ResolvedWaypoint[]> {
    const q = query.trim().toUpperCase();
    if (!q) return [];

    // Static search
    const results = STATIC_AIRPORTS.filter(a =>
      a.identifier.includes(q) || a.name.toUpperCase().includes(q),
    ).slice(0, limit);

    return results;
  }

  async findNearest(lat: number, lng: number, radiusNm: number, limit = 10): Promise<ResolvedWaypoint[]> {
    const DEG_TO_RAD = Math.PI / 180;
    const NM_PER_DEG = 60;

    return STATIC_AIRPORTS
      .map(a => {
        const dLat = (a.latitude - lat) * NM_PER_DEG;
        const dLon = (a.longitude - lng) * NM_PER_DEG * Math.cos(lat * DEG_TO_RAD);
        return { airport: a, dist: Math.sqrt(dLat * dLat + dLon * dLon) };
      })
      .filter(r => r.dist <= radiusNm)
      .sort((a, b) => a.dist - b.dist)
      .slice(0, limit)
      .map(r => r.airport);
  }
}
